/**
 * Customer Panel Accessibility Utilities
 * 
 * WCAG/W3C erişilebilirlik standartlarına uygun utility fonksiyonları
 */

/**
 * ARIA attributes for form fields
 */
export interface AriaAttributes {
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-errormessage'?: string;
}

/**
 * Form field için ARIA attributes oluşturur
 */
export function createFieldAriaAttributes(
  fieldId: string,
  label: string,
  required: boolean = false,
  hasError: boolean = false,
  errorId?: string,
): AriaAttributes {
  const attributes: AriaAttributes = {
    'aria-label': label,
  };

  if (required) {
    attributes['aria-required'] = true;
  }

  if (hasError && errorId) {
    attributes['aria-invalid'] = true;
    attributes['aria-describedby'] = errorId;
    attributes['aria-errormessage'] = errorId;
  }

  return attributes;
}

/**
 * Error message için ARIA attributes
 */
export function createErrorMessageAriaAttributes(fieldId: string): Record<string, string> {
  return {
    'aria-live': 'polite',
    'aria-atomic': 'true',
    id: `${fieldId}-error`,
  };
}

/**
 * Loading state için ARIA attributes
 */
export function createLoadingAriaAttributes(isLoading: boolean): Record<string, boolean | string> {
  return {
    'aria-busy': isLoading,
    'aria-live': isLoading ? 'polite' : 'off',
  };
}

/**
 * Button için ARIA attributes
 */
export function createButtonAriaAttributes(
  label: string,
  isLoading: boolean = false,
  disabled: boolean = false,
): Record<string, string | boolean> {
  return {
    'aria-label': isLoading ? `${label} (loading)` : label,
    'aria-disabled': disabled || isLoading,
    'aria-busy': isLoading,
  };
}

/**
 * Focus management - form field'a focus yapar
 */
export function focusField(fieldId: string): void {
  const field = document.getElementById(fieldId);
  if (field) {
    field.focus();
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

/**
 * Keyboard navigation - Enter key handler
 */
export function handleEnterKey(
  event: React.KeyboardEvent,
  callback: () => void,
  preventDefault: boolean = true,
): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    if (preventDefault) {
      event.preventDefault();
    }
    callback();
  }
}

/**
 * Skip to content link için ID
 */
export const SKIP_TO_CONTENT_ID = 'main-content';

